import { Component, computed, DestroyRef, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import dayjs from 'dayjs';
import { Observable, Subject, switchMap } from 'rxjs';
import { ManageRemoteWorkDialogComponent } from 'src/app/components/main/employees/activity/working-times-calendar/manage-remote-work-dialog/manage-remote-work-dialog.component';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import { DayOfWeekEnum } from 'src/app/enums/day-of-week.enum';
import { EmployeeRequestResponsibleForAnswerStatusEnum } from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';
import { RemoteWorkExceptionRequest } from 'src/app/models/remote_work_exception_request.model';
import { BooleanToCheckCrossPipe } from 'src/app/pipes/boolean-to-check-cross.pipe';
import { RemoteWorkWeekdayExceptionRequestStatusPipe } from 'src/app/pipes/remote-work-weekday-exception-request-status.pipe';
import { WeekDateToCustomLabelPipe } from 'src/app/pipes/week-date-to-custom-label.pipe';
import { ApiService, PermissionsService, UserService, UtilsService } from 'src/app/services';
import {
  RemoteWorkRequestReviewDialogComponent,
  RemoteWorkRequestReviewDialogData,
} from '../remote-work-request-review-dialog/remote-work-request-review-dialog.component';

@Component({
  selector: 'app-remote-work-management',
  templateUrl: './remote-work-management.component.html',
  styleUrl: './remote-work-management.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    WeekDateToCustomLabelPipe,
    RemoteWorkWeekdayExceptionRequestStatusPipe,
    BooleanToCheckCrossPipe,
  ],
})
export class RemoteWorkManagementComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  protected router: Router;
  protected fb: UntypedFormBuilder;
  protected activatedRoute: ActivatedRoute;
  protected utils: UtilsService;

  readonly EmployeeRequestResponsibleForAnswerStatusEnum = EmployeeRequestResponsibleForAnswerStatusEnum;

  readonly is_rrhh_view = computed((): boolean => this.router.url.includes('/rrhh/remote-work-management'));

  displayedColumns: string[] = [];

  readonly dataSource = new MatTableDataSource<RemoteWorkExceptionRequest>([]);

  override filterFormExtraParams!: {
    status: EmployeeRequestResponsibleForAnswerStatusEnum | '';
    responsible_user_id: number | '';
  };

  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly permissions = inject(PermissionsService);
  private readonly user = inject(UserService);

  protected readonly current_user_id = this.user.getCurrentUser()?.id;
  protected readonly is_rrhh_or_admin = toSignal(this.permissions.validateDepartmentObservable(['direction', 'rrhh']), {
    initialValue: false,
  });

  private readonly fetchTrigger$ = new Subject<void>();

  private readonly requestsResult = toSignal(
    this.fetchTrigger$.pipe(
      switchMap(
        (): Observable<ArrayResponse<RemoteWorkExceptionRequest>> =>
          this.api.getRemoteWorkExceptionRequests({
            ...this.filterForm.value,
            ...(!this.is_rrhh_view() ? { user_id: this.current_user_id } : {}),
            with: 'user',
            order_by: 'created_at:desc',
          })
      ),
      takeUntilDestroyed(this.destroy_ref)
    ),
    { initialValue: undefined }
  );

  constructor() {
    const router = inject(Router);
    const fb = inject(UntypedFormBuilder);
    const activatedRoute = inject(ActivatedRoute);
    const utils = inject(UtilsService);

    super(router, fb, activatedRoute, utils);

    this.router = router;
    this.fb = fb;
    this.activatedRoute = activatedRoute;
    this.utils = utils;

    const is_rrhh_view = router.url.includes('/rrhh/remote-work-management');

    const status_from_query = activatedRoute.snapshot.queryParamMap.get('status');

    const responsible_user_id_from_query = activatedRoute.snapshot.queryParamMap.get('responsible_user_id');

    const responsible_user_id =
      is_rrhh_view && responsible_user_id_from_query === null
        ? (this.current_user_id ?? '')
        : is_rrhh_view && responsible_user_id_from_query !== ''
          ? Number(responsible_user_id_from_query)
          : '';

    this.filterFormExtraParams = {
      status:
        (status_from_query as EmployeeRequestResponsibleForAnswerStatusEnum | null) ??
        (is_rrhh_view ? EmployeeRequestResponsibleForAnswerStatusEnum.PENDING : ''),
      responsible_user_id,
    };

    if (is_rrhh_view && responsible_user_id_from_query === null && this.current_user_id !== undefined) {
      void router.navigate([], {
        relativeTo: activatedRoute,
        queryParams: {
          responsible_user_id: this.current_user_id,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this.setDisplayedColumns();
    this.filterForm.removeControl('search');

    effect((): void => {
      const data = this.requestsResult();

      if (!data) {
        return;
      }

      this.dataSource.data = data.data;

      this.setMetadata(Number(data.meta.total), data.meta.current_page - 1, data.meta.per_page);
    });
  }

  override fetchData(): void {
    this.fetchTrigger$.next();
  }

  openCreateRemoteWorkExceptionDialog(): void {
    if (this.current_user_id === undefined) {
      return;
    }

    const from = dayjs().startOf('isoWeek').toDate();
    const to = dayjs().endOf('isoWeek').toDate();

    this.api
      .getEmployeeRemoteWork(this.current_user_id.toString(), from, to)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((days: Date[]): void => {
        const remote_work_days: Record<DayOfWeekEnum, boolean> = {
          [DayOfWeekEnum.Monday]: false,
          [DayOfWeekEnum.Tuesday]: false,
          [DayOfWeekEnum.Wednesday]: false,
          [DayOfWeekEnum.Thursday]: false,
          [DayOfWeekEnum.Friday]: false,
          [DayOfWeekEnum.Saturday]: false,
          [DayOfWeekEnum.Sunday]: false,
        };

        days.forEach((day: Date): void => {
          const day_name = dayjs(day).locale('en').format('dddd').toLowerCase() as DayOfWeekEnum;

          if (day_name in remote_work_days) {
            remote_work_days[day_name] = true;
          }
        });

        this.dialog
          .open(ManageRemoteWorkDialogComponent, {
            width: '500px',
            data: {
              remote_work_days,
              user_id: this.current_user_id!.toString(),
              from,
              to,
            },
            autoFocus: false,
          })
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroy_ref))
          .subscribe((): void => {
            this.fetchData();
          });
      });
  }

  openReviewRemoteWorkRequestDialog(remote_work_exception_request: RemoteWorkExceptionRequest): void {
    this.dialog
      .open(RemoteWorkRequestReviewDialogComponent, {
        width: '600px',
        data: {
          remote_work_exception_request,
          is_rrhh_view: this.is_rrhh_view(),
        } satisfies RemoteWorkRequestReviewDialogData,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((result: true | null): void => {
        if (result) {
          this.fetchData();
        }
      });
  }

  openApprovalStatusDialog(remote_work_exception_request: RemoteWorkExceptionRequest): void {
    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data: {
        requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.RemoteWorkWeekdayExceptionRequest,
        requestable_id: remote_work_exception_request.id,
        requestable_status: remote_work_exception_request.status,
      } as EmployeeRequestApprovalStatusDialogData,
    });
  }

  private setDisplayedColumns(): void {
    const shared_columns = [
      'semana empieza en',
      'status',
      'lunes',
      'martes',
      'miercoles',
      'jueves',
      'viernes',
      'sabado',
      'domingo',
      'actions',
    ];

    this.displayedColumns = this.is_rrhh_view() ? ['nombre', ...shared_columns] : shared_columns;
  }
}
